import { SignatureV4 } from "@aws-sdk/signature-v4";
import { Sha256 } from "@aws-crypto/sha256-js";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";

/**
 * API呼び出しのためのユーティリティ関数
 * 認証ヘッダーを自動的に追加するfetchラッパー
 */
import { fetchAuthSession } from "aws-amplify/auth";
// PRODUCT_DEV-902: 403 の理由。**相対パスで読む**（shared-ui の vitest に
// `@shared-domain` の別名が無い。`auth-api.ts` の contract 読み込みと同じ形）。
import { IP_NOT_ALLOWED_REASON } from "../../shared-domain/lib/ipRestriction";

/**
 * Cognitoから現在のIDトークンを取得する
 * @returns IDトークン文字列
 */
export const getIdToken = async (): Promise<string> => {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || "";
  } catch (error) {
    console.error("ID Token取得エラー:", error);
    throw new Error("認証情報の取得に失敗しました。再ログインしてください。", {
      cause: error,
    });
  }
};

/**
 * API呼び出しの基本設定を行う関数
 * @param options fetchオプション
 * @returns 認証ヘッダーを追加したfetchオプション
 */
export const createApiOptions = async (
  options: RequestInit = {},
): Promise<RequestInit> => {
  const idToken = await getIdToken();

  return {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${idToken}`,
    },
  };
};

/**
 * 401が強制リフレッシュ後も解消しない場合に遷移する認証ポータルURL。
 * 各 product が createAuthLoader 経由で setAuthPortalRedirectUrl で登録する。
 * 未登録(例: Portal 自身は loader 不使用)なら redirect せず、従来どおり throw のみ。
 *
 * PRODUCT_DEV-916: `beforeRedirect` は遷移の直前に呼ぶ（今の画面の URL を退避して、
 * ログイン後にそこへ戻すため）。省略したプロダクトは従来どおり。
 */
// PRODUCT_DEV-1129: 関数で渡すと飛ぶ瞬間に URL を作る（今の画面・テナントを URL に載せるため）。
let authPortalRedirectUrl: string | (() => string) | null = null;
let beforeAuthPortalRedirect: (() => void) | undefined;
export const setAuthPortalRedirectUrl = (
  url: string | (() => string) | null,
  beforeRedirect?: () => void,
): void => {
  authPortalRedirectUrl = url;
  beforeAuthPortalRedirect = beforeRedirect;
};

/**
 * 認証付き fetch。401 を受けたら強制リフレッシュ(forceRefresh)して 1 回だけ再試行する。
 * shared-ui TokenProvider の先制リフレッシュが(クロックスキュー/長時間アイドル等で)
 * 取りこぼした失効を回復し、不要な再ログインを防ぐ(PRODUCT_DEV-549)。
 * 認証ヘッダは createApiOptions が付与するため baseOptions には含めない。
 *
 * PRODUCT_DEV-631: export しているのは、403 を handleApiError の共通挙動
 * (`/403` へ遷移して後続を永久 pending にする) に委ねず、呼び出し側の画面内で
 * 扱いたいケースがあるため。通常のデータ取得は apiGet/apiPost 等を使うこと。
 */
export const authFetch = async (
  url: string,
  baseOptions: RequestInit = {},
): Promise<Response> => {
  const response = await fetch(url, await createApiOptions(baseOptions));
  if (response.status !== 401) {
    return response;
  }
  try {
    await fetchAuthSession({ forceRefresh: true });
  } catch (error) {
    console.error("401受信。強制リフレッシュに失敗:", error);
    return response;
  }
  // 新しいトークンで 1 回だけ再試行する
  return fetch(url, await createApiOptions(baseOptions));
};

/**
 * GET APIリクエスト
 * @param url エンドポイントURL
 * @param options fetchオプション（オプショナル）
 * @returns レスポンスのJSONデータ
 */
export const apiGet = async <T = unknown>(
  url: string,
  options: RequestInit = {},
): Promise<T> => {
  const response = await authFetch(url, options);

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json() as Promise<T>;
};

/**
 * POST APIリクエスト（JSON）
 * @param url エンドポイントURL
 * @param data 送信するデータ
 * @param options fetchオプション（オプショナル）
 * @returns レスポンスのJSONデータ
 */
export const apiPost = async <T = unknown, D = unknown>(
  url: string,
  data?: D,
  options: RequestInit = {},
): Promise<T> => {
  const response = await authFetch(url, {
    ...options,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    body: JSON.stringify(data || {}),
  });

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json() as Promise<T>;
};

/**
 * PATCH APIリクエスト（JSON）
 * @param url エンドポイントURL
 * @param data 送信するデータ
 * @param options fetchオプション（オプショナル）
 * @returns レスポンスのJSONデータ
 */
export const apiPatch = async <T = unknown, D = unknown>(
  url: string,
  data?: D,
  options: RequestInit = {},
): Promise<T> => {
  const response = await authFetch(url, {
    ...options,
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    body: JSON.stringify(data || {}),
  });

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json() as Promise<T>;
};

/**
 * PUT APIリクエスト（JSON・認証あり）
 *
 * PRODUCT_DEV-622: 既存の `apiPut` は presigned URL への直 PUT 用で `authFetch` を通さないため、
 * 認証付き API には使えない。`apiPatch` と同じ経路で method だけ PUT にしたものが必要だった
 * （API Gateway 側が PUT でしかルートを持たないエンドポイント向け）。
 *
 * @param url エンドポイントURL
 * @param data 送信するデータ
 * @param options fetchオプション（オプショナル）
 * @returns レスポンスのJSONデータ
 */
export const apiPutJson = async <T = unknown, D = unknown>(
  url: string,
  data?: D,
  options: RequestInit = {},
): Promise<T> => {
  const response = await authFetch(url, {
    ...options,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    body: JSON.stringify(data || {}),
  });

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json() as Promise<T>;
};

/**
 * DELETE APIリクエスト（JSON）
 * @param url エンドポイントURL
 * @param data 送信するデータ（オプショナル）
 * @param options fetchオプション（オプショナル）
 * @returns レスポンスのJSONデータ
 */
export const apiDelete = async <T = unknown, D = unknown>(
  url: string,
  data?: D,
  options: RequestInit = {},
): Promise<T> => {
  const response = await authFetch(url, {
    ...options,
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    body: data ? JSON.stringify(data) : undefined,
  });

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json() as Promise<T>;
};

/**
 * PUT APIリクエスト（任意のボディ形式に対応）
 * @template T レスポンス型
 * @param url エンドポイントURL
 * @param body 送信するデータ（File, Blob, JSON, FormDataなど）
 * @param options fetchオプション（オプショナル）
 * @returns パースされたJSONレスポンス（T型）
 */
export const apiPut = async <T = unknown>(
  url: string,
  body: BodyInit,
  options: RequestInit = {},
): Promise<T | void> => {
  const apiOptions: RequestInit = {
    ...options,
    method: "PUT",
    body,
    headers: {
      ...(options.headers || {}),
    },
  };

  const response = await fetch(url, apiOptions);

  console.log({ response });

  if (!response.ok) {
    await handleApiError(response);
  }

  const contentLength = response.headers.get("Content-Length");
  const contentType = response.headers.get("Content-Type");

  // JSON が返ってくると期待される場合のみパース
  if (contentLength !== "0" && contentType?.includes("application/json")) {
    try {
      return (await response.json()) as T;
    } catch {
      console.warn("レスポンスのJSONパースに失敗しましたが、無視します");
      return undefined;
    }
  }

  // ボディが空なら void を返す（問題なし）
  return undefined;
};

/**
 * POST APIリクエスト（FormData）
 * @param url エンドポイントURL
 * @param formData 送信するFormData
 * @param options fetchオプション（オプショナル）
 * @returns レスポンスのJSONデータ
 */
export const apiPostFormData = async <T = unknown>(
  url: string,
  formData: FormData,
  options: RequestInit = {},
): Promise<T> => {
  // FormDataを使用する場合は、Content-Typeヘッダーを自動的に設定するため、
  // 明示的に設定しないことで、ブラウザが適切なContent-Typeとboundaryを設定する
  const response = await authFetch(url, {
    ...options,
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json() as Promise<T>;
};

/**
 * POST APIリクエスト（Blob形式のレスポンス）
 * @param url エンドポイントURL
 * @param data 送信するデータ
 * @param options fetchオプション（オプショナル）
 * @returns Blobデータとヘッダー情報
 */
export const apiPostBlob = async <D = unknown>(
  url: string,
  data: D,
  options: RequestInit = {},
): Promise<{ blob: Blob; headers: Headers }> => {
  const response = await authFetch(url, {
    ...options,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    await handleApiError(response);
  }

  const blob = await response.blob();
  return { blob, headers: response.headers };
};

/**
 * 403 が IP 制限によるものなら `?reason=ip&ip=<送信元IP>` を返す（PRODUCT_DEV-902）。
 *
 * 判定は**サーバが返す `reason`**（機械が読む値）だけで行う。`message` の文言で
 * 判定すると、文言を直した瞬間に案内が壊れる。
 *
 * 本文を読めない・JSON でない・`reason` が違う場合は**空文字**を返し、
 * 呼び出し側は今までどおり `/403` へ行く。ここで例外を投げてはいけない
 * （403 の処理そのものが止まると、利用者は白い画面を見ることになる）。
 *
 * `response.clone()` を使うのは、この後で呼び出し側が本文を読むため
 * （Response の本文は一度しか読めない）。
 */
const ipRestrictionQuery = async (response: Response): Promise<string> => {
  try {
    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.includes("application/json")) return "";
    const body = (await response.clone().json()) as {
      reason?: unknown;
      sourceIp?: unknown;
    };
    if (body?.reason !== IP_NOT_ALLOWED_REASON) return "";
    const ip = typeof body.sourceIp === "string" ? body.sourceIp : "";
    return ip
      ? `?reason=ip&ip=${encodeURIComponent(ip)}`
      : "?reason=ip";
  } catch {
    return "";
  }
};

/**
 * APIエラー処理
 * @param response fetchレスポンス
 * @throws エラーメッセージ
 */
export const handleApiError = async (response: Response): Promise<never> => {
  // 403の場合は即座にリダイレクト（ループ防止のため403ページ上では実行しない）
  if (response.status === 403 && !window.location.pathname.includes("/403")) {
    const basePath = import.meta.env.BASE_URL || "/";
    const base = basePath.endsWith("/") ? `${basePath}403` : `${basePath}/403`;
    // PRODUCT_DEV-902: IP 制限で弾かれたときだけ、案内を出し分けるための理由を
    // クエリで渡す。**本文を読めなくても従来どおり `/403` へ行く**（ここで失敗させない）。
    //
    // クエリにするのは、この後の `window.location.href` が**フルリロード**だから。
    // sessionStorage に置くと、後で起きた無関係な 403 に古い理由が残りうる。
    const redirectUrl = `${base}${await ipRestrictionQuery(response)}`;
    console.error(
      `403 Forbidden on ${response.url}: リダイレクトします`,
      redirectUrl,
    );
    window.location.href = redirectUrl;
    // ブラウザの /403 ナビゲーション完了まで後続コードを止める。
    // throw すると loader の catch で React Router redirect が走り、/403 より優先されてループになる（PRODUCT_DEV-418）。
    await new Promise<never>(() => {});
  }

  // PRODUCT_DEV-549: 401が authFetch の強制リフレッシュ+再試行後も解消しない
  // = リフレッシュトークン失効等で復帰不能。認証ポータルへ遷移して再ログインを促す。
  // ただし `/auth/*`(whoami/exchange-code/me/refresh)の401は createAuthLoader の
  // 既存復帰フロー(auto/logout 制御つき)に委ね、二重リダイレクトを避ける。
  if (
    response.status === 401 &&
    authPortalRedirectUrl &&
    !response.url.includes("/auth/")
  ) {
    const target =
      typeof authPortalRedirectUrl === "function"
        ? authPortalRedirectUrl()
        : authPortalRedirectUrl;
    console.error(
      `401 Unauthorized on ${response.url}: 認証ポータルへリダイレクトします`,
      target,
    );
    // PRODUCT_DEV-916: ログイン後に今の画面へ戻すため、遷移の直前に退避する
    beforeAuthPortalRedirect?.();
    window.location.href = target;
    await new Promise<never>(() => {});
  }

  let errorMessage = `サーバーエラー: ${response.status}`;

  try {
    const contentType = response.headers.get("content-type");

    if (contentType && contentType.includes("application/json")) {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.message || errorMessage;
    } else {
      const errorText = await response.text();
      if (errorText) {
        errorMessage = `${errorMessage} - ${errorText.substring(0, 100)}`;
      }
    }
  } catch (e) {
    console.error("エラーレスポンスの解析に失敗:", e);
  }

  // PRODUCT_DEV-212: ステータスコードを Error に添付し、呼び出し側で 409 等を分岐できるようにする。
  const error = new Error(errorMessage) as Error & { status?: number };
  error.status = response.status;
  throw error;
};

/*
 * Lambda関数URL (POST) を受け取り、Idpoolを利用した署名付きURLを作成して実行する関数
 * 関数URLにCognito認証を付与する際に使用する
 */
export const apiSigV4URL = async <T = unknown, D = unknown>(
  url: string,
  data: D,
  options: RequestInit = {},
  awsSettings: {
    region?: string;
    userPoolId: string;
    idPoolId: string;
  },
): Promise<T> => {
  console.log({ url, data, options, awsSettings });

  // Cognitoクレデンシャルプロバイダーを設定
  const region = awsSettings.region || "ap-northeast-1";
  const identityProviderKey = `cognito-idp.ap-northeast-1.amazonaws.com/${awsSettings.userPoolId}`;
  // client インスタンスを渡さず clientConfig で組み立てさせる (PRODUCT_DEV-629)。
  // @aws-sdk/credential-provider-cognito-identity は内部の Cognito クライアントを
  // @aws-sdk/client-cognito-identity から @aws-sdk/nested-clients へ移しており、
  // こちらで new した CognitoIdentityClient は「別パッケージの同名クラス」になって
  // 型が合わない (TS2322)。clientConfig ならプロバイダ側が自前で生成するため
  // パッケージの入れ替わりに追従でき、クライアントの二重同梱も避けられる。
  const credentialsProvider = fromCognitoIdentityPool({
    clientConfig: { region },
    identityPoolId: awsSettings.idPoolId,
    logins: {
      [identityProviderKey]: async () => await getIdToken(),
    },
  });

  // SigV4 署名インスタンスを作成
  const sigV4 = new SignatureV4({
    service: "lambda",
    region,
    credentials: credentialsProvider,
    sha256: Sha256,
  });

  // SigV4署名付きリクエストを作成
  const urlObj = new URL(url);
  const body = JSON.stringify(data);

  const request = {
    method: "POST",
    hostname: urlObj.hostname,
    path: urlObj.pathname + url.search,
    protocol: urlObj.protocol,
    headers: {
      "Content-Type": "application/json",
      host: urlObj.hostname,
    },
    body,
  };
  // console.log({ request });

  // リクエストに署名を追加
  const signedRequest = await sigV4.sign(request);
  // console.log({ signedRequest });

  // 署名付きヘッダーでfetchを実行
  const response = await fetch(url, {
    ...options,
    method: "POST",
    headers: {
      ...options.headers,
      ...signedRequest.headers,
    },
    body,
  });
  // console.log({ response });

  return response.json() as Promise<T>;
};
